import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { HubHost } from './types';

// When the machine is short of memory (macOS memory_pressure's free %) or swapping, it slows every run on it.
export const memoryTone = (freePct: number | null) => (freePct == null ? undefined : freePct < 10 ? '#B91C1C' : freePct < 25 ? '#B45309' : undefined);
export const swapTone = (swapGb: number | null, totalGb: number) => (swapGb == null ? undefined : swapGb > totalGb / 2 ? '#B91C1C' : swapGb > 1 ? '#B45309' : undefined);

/** One hub's machine: CPU load, memory, swap, disk, and its runs against its slots. Amber or red when it's struggling. */
export default function MachineCell({ host }: { host: HubHost }) {
  const m = host.machine;
  if (!m) return <Typography variant="caption" color="text.secondary">Not reported (an older hub)</Typography>;
  const parts: Array<{ text: string; tone?: string; tip?: string }> = [
    { text: `CPU ${m.cpu.load1} load / ${m.cpu.cores} cores (${m.cpu.pct}%)`, tip: `5-minute load ${m.cpu.load5}` },
    { text: m.memory.freePct == null ? `Memory ${m.memory.totalGb} GB` : `Memory ${m.memory.freePct}% free of ${m.memory.totalGb} GB`, tone: memoryTone(m.memory.freePct), tip: "macOS's own system-wide free memory (what memory_pressure reports)" },
    ...(m.swapUsedGb == null ? [] : [{ text: `swap ${m.swapUsedGb} GB`, tone: swapTone(m.swapUsedGb, m.memory.totalGb), tip: 'Swap in use: memory pushed to disk, which slows everything' }]),
    ...(m.diskFreeGb == null ? [] : [{ text: `disk ${m.diskFreeGb} GB free` }]),
    ...(host.slots ? [{ text: `runs ${host.runs ?? 0}/${host.slots.preview + host.slots.other}`, tip: `${host.slots.previewRunning} of ${host.slots.preview} preview slots in use; ${host.slots.other} other slots` }] : []),
  ];
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1 }}>
      {parts.map((p, i) => (
        <Tooltip key={p.text} title={p.tip ?? ''} arrow disableHoverListener={!p.tip}>
          <Typography variant="caption" sx={{ color: p.tone, fontWeight: p.tone ? 600 : undefined, whiteSpace: 'nowrap' }}>{p.text}{i < parts.length - 1 ? ' ·' : ''}</Typography>
        </Tooltip>
      ))}
    </Box>
  );
}
